import { useState, useRef, useCallback, useEffect } from 'react';
interface UseDragLogicProps {
  isMobile: boolean;
  selectedDate: Date | null;
  calendarContainerRef: React.RefObject<HTMLDivElement>;
  calendarGridRef: React.RefObject<HTMLDivElement>;
  getRowHeight: () => number;
  getAllRowsHeight: () => number;
  getSelectedDateRow: (date: Date | null) => number;
  actualRows: number;
  currentDate: Date;
}

export const useDragLogic = ({
  isMobile,
  selectedDate,
  calendarContainerRef,
  calendarGridRef: _calendarGridRef,
  getRowHeight,
  getAllRowsHeight,
  getSelectedDateRow,
  actualRows: _actualRows,
  currentDate,
}: UseDragLogicProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [initialCalendarHeight, setInitialCalendarHeight] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [calendarHeight, setCalendarHeight] = useState<number | null>(null);
  
  const dragStartTimeRef = useRef<number>(0);
  const lastMoveTimeRef = useRef<number>(0);
  const lastMoveYRef = useRef<number>(0);
  const velocityRef = useRef<number>(0);

  const getSelectedDateRowHeight = useCallback((): number => {
    return getRowHeight();
  }, [getRowHeight]);

  const handleDragStart = useCallback((e: React.TouchEvent) => {
    if (!isMobile || !calendarContainerRef.current) return;
    e.stopPropagation();
    
    setIsDragging(true);
    const startY = e.touches[0].clientY;
    setDragStartY(startY);
    setDragOffset(0);
    
    const startTime = Date.now();
    dragStartTimeRef.current = startTime;
    lastMoveTimeRef.current = startTime;
    lastMoveYRef.current = startY;
    velocityRef.current = 0;
    
    const currentHeight = calendarContainerRef.current.offsetHeight;
    setInitialCalendarHeight(currentHeight);
    
    if (calendarHeight === null) {
      setCalendarHeight(currentHeight);
    }
  }, [isMobile, calendarHeight, calendarContainerRef]);

  const handleDragMove = useCallback((e: React.TouchEvent) => {
    if (!isMobile || !isDragging || initialCalendarHeight === null) return;
    e.preventDefault();
    e.stopPropagation();
    
    const currentY = e.touches[0].clientY;
    const currentTime = Date.now();
    
    const timeDelta = currentTime - lastMoveTimeRef.current;
    if (timeDelta > 0) {
      const distanceDelta = lastMoveYRef.current - currentY;
      const currentVelocity = distanceDelta / timeDelta;
      velocityRef.current = currentVelocity;
    }
    
    lastMoveTimeRef.current = currentTime;
    lastMoveYRef.current = currentY;
    
    const dragDistance = dragStartY - currentY;
    setDragOffset(dragDistance);
    
    const newHeight = initialCalendarHeight - dragDistance;
    const minHeight = selectedDate ? getSelectedDateRowHeight() : getRowHeight();
    const maxHeight = getAllRowsHeight();
    const clampedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
    setCalendarHeight(clampedHeight);
  }, [isMobile, isDragging, dragStartY, initialCalendarHeight, selectedDate, getSelectedDateRowHeight, getRowHeight, getAllRowsHeight]);

  const handleDragEnd = useCallback(() => {
    if (!isMobile || !isDragging || initialCalendarHeight === null) return;
    
    const minHeight = selectedDate ? getSelectedDateRowHeight() : getRowHeight();
    const maxHeight = getAllRowsHeight();
    const VELOCITY_THRESHOLD = 0.5;
    const velocity = velocityRef.current;
    
    if (Math.abs(velocity) > VELOCITY_THRESHOLD) {
      if (velocity > 0) {
        setIsDragging(false);
        setDragStartY(0);
        setDragOffset(0);
        setInitialCalendarHeight(null);
        setCalendarHeight(minHeight);
        return;
      } else {
        setIsDragging(false);
        setDragStartY(0);
        setDragOffset(0);
        setInitialCalendarHeight(null);
        setCalendarHeight(null);
        return;
      }
    }
    
    const finalHeight = initialCalendarHeight - dragOffset;
    let clampedHeight = Math.max(minHeight, Math.min(maxHeight, finalHeight));
    
    if (Math.abs(clampedHeight - minHeight) < 5) {
      clampedHeight = minHeight;
    }
    
    if (Math.abs(clampedHeight - maxHeight) < 5) {
      setCalendarHeight(null);
    } else {
      setCalendarHeight(clampedHeight);
    }
    
    setIsDragging(false);
    setDragStartY(0);
    setDragOffset(0);
    setInitialCalendarHeight(null);
  }, [isMobile, isDragging, dragOffset, initialCalendarHeight, selectedDate, getSelectedDateRowHeight, getRowHeight, getAllRowsHeight]);

  useEffect(() => {
    if (isMobile) {
      setCalendarHeight(null);
    }
  }, [currentDate, isMobile]);

  const getCalendarHeight = useCallback((): string => {
    if (!isMobile) return 'auto';
    
    if (isDragging) {
      if (calendarHeight !== null) {
        return `${calendarHeight}px`;
      }
      if (initialCalendarHeight !== null) {
        const newHeight = initialCalendarHeight - dragOffset;
        const minHeight = selectedDate ? getSelectedDateRowHeight() : getRowHeight();
        const maxHeight = getAllRowsHeight();
        return `${Math.max(minHeight, Math.min(maxHeight, newHeight))}px`;
      }
    }
    
    if (calendarHeight !== null) {
      return `${calendarHeight}px`;
    }
    return 'auto';
  }, [isMobile, isDragging, calendarHeight, initialCalendarHeight, dragOffset, selectedDate, getSelectedDateRowHeight, getRowHeight, getAllRowsHeight]);

  const getCalendarTransform = useCallback((): string => {
    if (!isMobile || !selectedDate || calendarHeight === null) {
      return 'translateY(0)';
    }
    
    const selectedRowIndex = getSelectedDateRow(selectedDate);
    const rowHeight = getRowHeight();
    const maxHeight = getAllRowsHeight();
    const hiddenRows = (maxHeight - calendarHeight) / rowHeight;
    
    let translateY = 0;
    
    if (hiddenRows > 0) {
      if (hiddenRows < selectedRowIndex) {
        translateY = -hiddenRows * rowHeight;
      } else {
        translateY = -selectedRowIndex * rowHeight;
      }
    }
    
    return `translateY(${translateY}px)`;
  }, [isMobile, selectedDate, calendarHeight, getSelectedDateRow, getRowHeight, getAllRowsHeight]);

  return {
    isDragging,
    calendarHeight,
    getCalendarHeight,
    getCalendarTransform,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
  };
};
